import { newGuestExperience } from "@/services/homepageExperiment";
import { useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { useUser } from "@/redux/user";
import {
  useRegister,
  registerStore,
  setUsername,
  setUsernameError,
} from "@/redux/register";
import { generateGuestName } from "@/util/guestNames";
import { validateUsername } from "@/util/validate";
import { errorCodes } from "@chesshotel/constants";

export default function GuestNameNotice() {
  const { t } = useTranslation("common");
  const { locale } = useRouter();
  const { isGuest, displayName } = useUser();
  const { username, usernameError, step } = useRegister();
  const unavailable = !!usernameError;
  const suggestions = useMemo(() => {
    if (!unavailable) return [];
    const names: string[] = [username];
    for (let i = 0; i < 3; i++) names.push(generateGuestName(names));
    return names.slice(1);
  }, [unavailable, locale, username]);
  if (
    !newGuestExperience() ||
    !isGuest ||
    !displayName ||
    !unavailable ||
    !["username", "socialUsername"].includes(step)
  )
    return null;
  return (
    <aside
      className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm"
      aria-live="polite"
    >
      {unavailable ? (
        <>
          <p className="mt-3 text-slate-600">
            {t("guestIdentity.registerTaken")}
          </p>
          <p className="mt-2 font-medium">
            {t("guestIdentity.registerAlternatives")}
          </p>
          <div className="mt-1 flex flex-wrap gap-2">
            {suggestions.map((name) => (
              <button
                type="button"
                key={name}
                className="min-h-[44px] rounded-md border border-slate-300 bg-white px-3 text-altblue-600"
                onClick={async () => {
                  setUsername(name);
                  setUsernameError(null);
                  const failure = await validateUsername(name, locale);
                  if (registerStore.getState().username === name)
                    setUsernameError(failure);
                }}
              >
                <bdi>{name}</bdi>
              </button>
            ))}
          </div>
          {usernameError === errorCodes.ERROR_USERNAME_NOT_AVAILABLE && (
            <Link
              href="/signIn"
              className="mt-2 inline-flex min-h-[44px] items-center text-altblue-600 underline"
            >
              {t("Log in")}
            </Link>
          )}
        </>
      ) : (
        <p className="mt-3 text-slate-600">
          {t("guestIdentity.registerAvailable", {
            name: username || displayName,
          })}
        </p>
      )}
    </aside>
  );
}
